/**
 * @file Filter chips + search input for the Learners panel.
 * @module @labcraft/admin/ui/learners/learners-filters
 *
 * Status chips are a segmented control rendered as `<button>`s with
 * `aria-pressed`. The search input is uncontrolled-style (the parent
 * holds the query string and forwards it back); the parent applies
 * the 180 ms debounce before issuing a fetch.
 */

import type { JSX } from 'react'
import type { LearnerStatus } from '../../shared/routes.ts'
import { TagMultiSelect } from './tag_filter.tsx'

/** Status filter union; `'all'` means no filter. */
export type StatusFilter = LearnerStatus | 'all'

/** Props for {@link LearnersFilters}. */
export type LearnersFiltersProps = {
  status: StatusFilter
  onStatus: (status: StatusFilter) => void
  query: string
  onQuery: (query: string) => void
  tags: string[]
  onTags: (tags: string[]) => void
  tagsNot: string[]
  onTagsNot: (tags: string[]) => void
  tagSuggestions: string[]
}

const STATUSES: { id: StatusFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'inactive', label: 'Inactive' },
  { id: 'active', label: 'Active' },
  { id: 'banned', label: 'Banned' },
]

/** Render the subbar (chips + search + tag filters). */
export function LearnersFilters({
  status,
  onStatus,
  query,
  onQuery,
  tags,
  onTags,
  tagsNot,
  onTagsNot,
  tagSuggestions,
}: LearnersFiltersProps): JSX.Element {
  return (
    <div className="subbar">
      <div className="chips" role="group" aria-label="Filter by status">
        {STATUSES.map(s => (
          <button
            key={s.id}
            type="button"
            className="chip"
            aria-pressed={status === s.id}
            onClick={() => {
              onStatus(s.id)
            }}>
            {s.label}
          </button>
        ))}
      </div>
      <TagMultiSelect
        label="Tagged"
        selected={tags}
        onChange={onTags}
        suggestions={tagSuggestions}
        datalistId="lc-learners-tags-include"
        placeholder="tag…"
      />
      <TagMultiSelect
        label="Not tagged"
        selected={tagsNot}
        onChange={onTagsNot}
        suggestions={tagSuggestions}
        datalistId="lc-learners-tags-exclude"
        placeholder="tag…"
      />
      <label className="search">
        <span className="search__icon" aria-hidden>
          <svg
            width="13"
            height="13"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round">
            <circle cx="7" cy="7" r="4.5" />
            <path d="M10.5 10.5L13.5 13.5" />
          </svg>
        </span>
        <input
          type="search"
          data-admin-search
          placeholder="Search email or name…"
          value={query}
          onChange={e => {
            onQuery(e.target.value)
          }}
          aria-label="Search learners"
        />
        <span className="search__hint">
          <span className="kbd">/</span>
        </span>
      </label>
    </div>
  )
}
